import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { formatCurrencyUSD, formatNumber } from "@/lib/format";
import type { ReadingTypeCostRow } from "@/api/admin/economics";

const cardStyle: CSSProperties = {
  background: "var(--d-ink-2)",
  border: "1px solid var(--d-line)",
  borderRadius: 22,
  padding: 16,
};
const titleStyle: CSSProperties = {
  fontFamily: "var(--font-display)", fontSize: 16, color: "var(--d-fg)", marginBottom: 4,
};
const noteStyle: CSSProperties = {
  fontFamily: "var(--font-body)", fontSize: 11, color: "var(--d-fg-dim)", marginBottom: 12,
};
const tableStyle: CSSProperties = { width: "100%", borderCollapse: "collapse" };
const thStyle: CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.08em",
  color: "var(--d-fg-dim)", textAlign: "right", paddingBottom: 8,
};
const tdStyle: CSSProperties = {
  fontFamily: "var(--font-mono)", fontSize: 12, fontVariantNumeric: "tabular-nums",
  color: "var(--d-fg)", textAlign: "right", padding: "8px 0",
  borderTop: "1px solid var(--d-line)",
};
const leftStyle: CSSProperties = { textAlign: "left" };

const DASH = "—";

export function ReadingTypeCosts({
  rows, locale,
}: { rows: ReadingTypeCostRow[]; locale: string }): JSX.Element {
  const { t } = useTranslation("admin");
  return (
    <div style={cardStyle} data-testid="reading-type-costs">
      <div style={titleStyle}>{t("economics.by_reading_type.title")}</div>
      <div style={noteStyle}>{t("economics.by_reading_type.note")}</div>
      {rows.length === 0 ? (
        <div style={noteStyle}>{t("economics.by_reading_type.no_data")}</div>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={{ ...thStyle, ...leftStyle }}>{t("economics.by_reading_type.col.type")}</th>
              <th style={thStyle}>{t("economics.by_reading_type.col.readings")}</th>
              <th style={thStyle}>{t("economics.by_reading_type.col.tokens_out")}</th>
              <th style={thStyle}>{t("economics.by_reading_type.col.cost")}</th>
              <th style={thStyle}>{t("economics.by_reading_type.col.pairs")}</th>
              <th style={thStyle}>{t("economics.by_reading_type.col.per_pair")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.reading_type} data-testid={`reading-type-row-${r.reading_type}`}>
                <td style={{ ...tdStyle, ...leftStyle }}>{r.reading_type}</td>
                <td style={tdStyle}>{formatNumber(r.readings, locale)}</td>
                <td style={tdStyle}>{formatNumber(r.tokens_out, locale)}</td>
                <td style={tdStyle}>{formatCurrencyUSD(r.cost_usd, locale)}</td>
                <td style={tdStyle}>
                  {r.distinct_pairs === null ? DASH : formatNumber(r.distinct_pairs, locale)}
                </td>
                <td style={tdStyle}>
                  {r.cost_per_pair_usd === null ? DASH : formatCurrencyUSD(r.cost_per_pair_usd, locale)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
